import { BxfIcon, BxfLink, BxfTargetAtrEnum } from '@blk/bxf-react-components';
import { memo, useMemo } from 'react';
import Markdown from 'react-markdown';
import { SERVER_MESSAGE_RESPONSE_CONTENT_PDF_LINK } from '../constants';
import { useMarkdownOverride } from '../hooks';
import { ChatBlkPromptSource } from '../types';
import { createElementOverride } from '../utils';
import { ServerMessageImage, SourcePopover } from './';

interface MarkdownRendererProps {
    sources: Array<ChatBlkPromptSource>;
    id: string;
    response: string;
    onImgLoad: () => void;
}

/**
 * Renders markdown content with custom components.
 * @param sources - The sources for the markdown content.
 * @param response - The markdown content to render.
 * @param id - The unique identifier for the markdown content.
 * @param onImgLoad - The callback function to handle image load.
 * @returns The rendered markdown content.
 */
const MarkdownRenderer = ({
    sources,
    response,
    id,
    onImgLoad,
}: MarkdownRendererProps): JSX.Element => {
    const {
        overriddenElements,
        createLinkOverride,
        isSource,
        preprocessMarkdown,
        shouldRenderPDFIcon,
    } = useMarkdownOverride(id, sources);

    // TODO: uncomment to enable progressive loading
    // const { text } = useProgressiveLoader(response, id);
    const text = response;

    const overrides = overriddenElements.reduce((acc, tag) => {
        acc[tag] = (tagname: JSX.Element) =>
            createElementOverride(
                tag,
                tagname as JSX.Element & { children: JSX.Element[] }
            );
        return acc;
    }, {});

    const renderLink = (
        href: string,
        children: JSX.Element,
        linkNode: Element
    ) => {
        if (isSource(children, href)) {
            return (
                <SourcePopover
                    linkNode={linkNode}
                    href={href}
                    sources={sources}
                />
            );
        }

        const linkOverride = createLinkOverride(href, children, linkNode);
        const { label } = linkOverride;
        return shouldRenderPDFIcon(href) ? (
            <span className={SERVER_MESSAGE_RESPONSE_CONTENT_PDF_LINK}>
                <BxfIcon type={'bxf-pdf'} />
                <BxfLink
                    isCustomPopover
                    target={BxfTargetAtrEnum.BLANK}
                    dataLinkEvent={`chatblk | result | link click | ${label}`}
                    {...linkOverride}
                />
            </span>
        ) : (
            <BxfLink
                target={BxfTargetAtrEnum.BLANK}
                dataLinkEvent={`chatblk | result | link click | ${label}`}
                {...linkOverride}
            />
        );
    };

    const markdownComponents = useMemo(() => {
        return {
            ...overrides,
            img: ({ src = '', alt = null }) => (
                <ServerMessageImage
                    src={src}
                    alt={alt}
                    queryResponseId={id}
                    onImgLoad={onImgLoad}
                />
            ),
            a: ({ node: linkNode, href = '', children = null }) => {
                return renderLink(href, children, linkNode);
            },
        };
    }, [overrides, isSource, preprocessMarkdown, shouldRenderPDFIcon]);

    return (
        <Markdown components={markdownComponents}>
            {preprocessMarkdown(text)}
        </Markdown>
    );
};

export default memo(MarkdownRenderer, (prevProps, nextProps) => {
    return (
        prevProps.response === nextProps.response &&
        prevProps.sources === nextProps.sources &&
        prevProps.id === nextProps.id
    );
});
